import React from "react";
import { makeStyles } from "tss-react/mui";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@miplatform/constants";
import { LoaderType } from "@miplatform/types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  GridLegacy,
  Typography,
} from "@mui/material";
import { MiButton } from "../MiButton/MiButton";
import { MiLoader } from "../MiLoader/MiLoader";
import { ModalHeader } from "../MiModal/ModalHeader";

/**
 * A reusable modal confirmation dialog component with customizable buttons and content.
 *
 * @param {Object} props - The component props
 * @param {boolean} props.open - Controls whether the modal is visible
 * @param {Object} [props.titleIcon] - Icon to display next to the title
 * @param {string} [props.title] - The title text displayed in the modal header
 * @param {string} props.description - Description text displayed in the modal body
 * @param {boolean} [props.disableClick=false] - Disables the positive and secondary positive buttons
 * @param {string} [props.textNegative="Cancel"] - Text for the negative/cancel button
 * @param {string} [props.textPositive="Confirm"] - Text for the primary positive button
 * @param {string} [props.textSecondaryPositive="Accept"] - Text for the secondary positive button
 * @param {Function} props.handleNegative - Callback function for negative button click and modal close
 * @param {Function} props.handlePositive - Callback function for positive button click
 * @param {Function} [props.handleSecondaryPositive] - Optional callback for secondary positive button
 * @param {string} [props.buttonPositiveColor=BUTTON_COLORS.PRIMARY] - Color variant for the positive button
 * @param {string} [props.buttonSecondaryPositiveColor=BUTTON_COLORS.PRIMARY] - Color variant for the secondary positive button
 * @param {boolean} [props.hideNegative] - Hides the negative/cancel button when true
 * @param {Function} [props.dismissOnBackdropClick] - Optional callback for backdrop click to dismiss the modal
 * @param {React.ReactNode} [props.children] - Additional content to render inside the modal body
 * @param {boolean} [props.loading] - Shows a loading indicator when true
 * @param {Object} [props.sx] - Custom styles to apply to the Dialog component
 * @returns {React.ReactElement} The modal confirmation dialog component
 */
export function MiModalConfirm({
  open,
  titleIcon,
  title,
  description,
  disableClick = false,
  textNegative = "Cancel",
  textPositive = "Confirm",
  textSecondaryPositive = "Accept",
  handleNegative,
  handlePositive,
  handleSecondaryPositive,
  buttonPositiveColor = BUTTON_COLORS.PRIMARY,
  buttonSecondaryPositiveColor = BUTTON_COLORS.PRIMARY,
  hideNegative,
  dismissOnBackdropClick = null,
  children,
  loading,
  sx,
}) {
  const { classes, theme } = useStyles();

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      onClose={dismissOnBackdropClick ? () => dismissOnBackdropClick() : null}
      fullWidth
      sx={sx}
    >
      <ModalHeader
        title={title}
        titleIcon={titleIcon}
        closeModal={handleNegative}
        loading={loading}
      />
      <MiLoader isLoading={loading} type={LoaderType.Linear} />
      <DialogContent className={classes.descriptionSection}>
        <Typography className={classes.description}>{description}</Typography>
        {children}
      </DialogContent>
      <DialogActions className={classes.footerSection}>
        <GridLegacy
          container
          direction="row"
          justifyContent="flex-end"
          spacing={3}
        >
          {!hideNegative && (
            <GridLegacy item>
              <MiButton
                title={textNegative}
                variant={BUTTON_VARIANTS.TEXT}
                color={BUTTON_COLORS.BLACK}
                onClick={handleNegative}
                data-testid="cancel-button"
                disabled={loading}
                style={{
                  textDecoration: "underline",
                  textUnderlineOffset: theme.spacing(1),
                }}
              />
            </GridLegacy>
          )}
          {!!handleSecondaryPositive && (
            <GridLegacy item>
              <MiButton
                title={textSecondaryPositive}
                variant={BUTTON_VARIANTS.OUTLINED}
                color={buttonSecondaryPositiveColor}
                onClick={handleSecondaryPositive}
                disabled={disableClick || loading}
                data-testid="save-button"
              />
            </GridLegacy>
          )}
          <GridLegacy item>
            <MiButton
              title={textPositive}
              variant={BUTTON_VARIANTS.CONTAINED}
              color={buttonPositiveColor}
              onClick={handlePositive}
              disabled={disableClick || loading}
              data-testid="confirm-button"
            />
          </GridLegacy>
        </GridLegacy>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles()((theme) => ({
  descriptionSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(0, 3),
  },
  description: {
    color: theme.palette.grey.medium,
    fontSize: theme.fontSize.medium,
  },
  footerSection: {
    padding: theme.spacing(2, 3),
  },
}));
